/**
 * Today — the recruiter's daily worklist and the CRM landing page.
 *
 * Renders GET /api/crm/worklist as prioritized buckets, top to bottom in the
 * order they should be worked, in the two ATTENTION TIERS (backend
 * services/crmAttention.js; owner 2026-09-28: "the recruiters need less noise
 * ... ongoing conversations are always followed up on without delay or new
 * leads that are 'hot' and need immediate attention"):
 *
 * NEEDS ACTION NOW (the header chip and the sidebar badge count only these)
 *   My tasks      (reused MyTasksPanel — overdue / due-today reminders)
 *   Callbacks     booked call times overdue or in the next 24h, with one-tap
 *                 outcomes (the patient is expecting these — never miss them)
 *   Needs reply   inbound messages waiting on us, oldest first
 *   New leads     HOT new leads: came in (or re-submitted a form) in the last
 *                 few days and have not answered yet (speed-to-lead)
 *   Keep the conversation going
 *                 they wrote to us in the last week, we answered, they went
 *                 quiet, and the next nudge is due
 *
 * KEEP TRYING (below, never in the badge): due another try, but nobody is
 * waiting on us
 *   Your outreach lists, the Keep trying list (FollowUpsDuePanel: no answer
 *   yet, gone quiet, never contacted; collapsed until opened) and Going stale
 *   (mid-pipeline leads with no recent activity).
 *
 * Two lanes above the contact buckets are not lead buckets: the agent Review
 * queue, and "AI reviews ready" — medical-history reviews THIS user started
 * that still want a human. That one is scoped by who pressed the button rather
 * than who owns the lead, and clears on sign-off (see crmReviewReady).
 *
 * "Mentions" (docs/plans/crm-report-builder-search-collab-plan.md B3.4) follows
 * "AI reviews ready": notes where a teammate named this user, and replies to
 * notes they wrote, until read. Scoped to the person mentioned, like the review
 * lane, and rendered by mentions/MentionsLane from this page's own worklist read.
 *
 * "Your outreach lists" (docs/crm-outreach-lists-plan.md §B7) opens the Keep
 * trying group: one row per list a manager gave this recruiter, with its own
 * Work through. A list person who replies is in Needs reply like anyone else.
 * List leads are kept OUT of New leads / Follow-ups / Going stale server-side
 * (the list is how they get worked), so the section is where they show up here.
 * It reads the shell's counts rather than fetching, and reports its servable
 * count so "all caught up" never renders above people still to call.
 *
 * Scope is "mine + unassigned" server-side: unassigned leads appear on every
 * recruiter's list on purpose — coverage beats tidy ownership. Rows click
 * through to the contact; the list refetches on focus-relevant events
 * (crm:stats-changed) and a 60s poll, same rhythm as the sidebar badges.
 *
 * Out-of-office coverage (docs/crm-out-of-office-coverage-plan.md): while a
 * teammate is out, the server widens these buckets with the leads the cover
 * hash gave this viewer, each row carrying a `coverage` ref. Nothing about
 * ownership changed, so every such row wears a <CoverageBadge> — otherwise the
 * widened list reads as a silent reassignment and the same patient gets called
 * twice the day the owner returns.
 *
 * PHONES (useIsPhone, below `sm`): the "N need action now" chip sits beside
 * the short title and whose-leads, Work through and refresh make ONE toolbar
 * row under the date (three stacked rows before); a lead row's bucket chip
 * ("waiting 2h", "new · 40m") ends its name line instead of taking a column
 * beside the snooze button, so the reply, why-line and study chips run the
 * row's width; the Keep trying group's explanatory line is desktop-only.
 * Every control is mounted once per tier; desktop renders as before.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  CalendarClock,
  Check,
  CheckCircle2,
  MessagesSquare,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Hourglass,
  Loader2,
  Mail,
  MessageCircleWarning,
  MessageSquare,
  PhoneMissed,
  Sparkles,
  Sun,
  Undo2,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import api from '../../services/api';
import { ContactListItem, WorklistCallback, WorklistResponse, contactName, leadArrivedAt } from '../types';
import { STATS_CHANGED_EVENT, getPref, lifecycleMeta, needsReply, notifyStatsChanged, relTime, setPref, useIsPhone, useTeam } from '../lib';
import { useAuth } from '../../contexts/AuthContext';
import { FilterMultiSelect } from '../components/filters';
import { UNASSIGNED, defaultOwnerSelection, type OwnerSelection } from '../workQueueLogic';
import { Alert } from '../components/ui';
import { CollisionTriageCard, FollowUpsDuePanel, MyTasksPanel, PageHeader, RefreshButton, SnoozeButton } from '../components';
import CoverageBadge, { coverageOf } from '../components/CoverageBadge';
import { StudyChips, RowLink, useRowNav } from '../components/shared';
import { ESCALATION_KIND_LABEL } from '../components/escalationDisplay';
import { callbackOrigin } from '../components/scheduler/schedulerLogic';
import { schedulerReviewRow } from '../components/scheduler/escalationCopy';
import { useWorkQueue } from '../WorkQueueContext';
import OutreachTodaySection from '../outreach/OutreachTodaySection';
import MentionsLane from '../mentions/MentionsLane';
import { briefReasonLabel } from '../brief/briefLogic';
import type { Brief } from '../brief/types';

/** Clickable worklist row (callback / needs-reply / new-lead rows). */
const worklistRowCls = 'flex cursor-pointer items-center gap-3 py-3 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20';

/** Row label for an agent review item (escalation kinds + the awaiting-review synthetic). */
const agentReviewLabel = (kind: string): string =>
  kind === 'eligibility_review' ? 'Eligibility review' : ESCALATION_KIND_LABEL[kind] || kind.replace(/_/g, ' ');

/** Verdict wording for a finished AI medical-history review — the same labels
 *  the lead profile's review card and the overlay card use. */
const reviewResultLabel = (result: string | null): string => {
  switch (result) {
    case 'eligible': return 'Potentially eligible';
    case 'excluded': return 'Excluded';
    case 'review_required': return 'Needs review';
    default: return 'Incomplete';
  }
};

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** "Mon, Jun 15 at 3:00 PM" / "3:00 PM" in the viewer's locale. */
function slotLabel(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} ${time}`;
}

/**
 * The small "AI" mark beside a tier-1 row's context line when the AI brief wrote
 * that line (docs/plans/crm-brief-plan.md B6). Muted on purpose: the words are
 * the signal, the mark only says where they came from.
 */
function BriefMark() {
  return (
    <span
      role="img"
      aria-label="Suggested by the AI brief"
      title="Suggested by the AI brief"
      data-brief-mark=""
      className="inline-flex flex-shrink-0 text-gray-400 dark:text-gray-500"
    >
      <Sparkles className="h-3 w-3" aria-hidden="true" />
    </span>
  );
}

/**
 * A Needs reply or Keep the conversation going row's line in the lead's own
 * words: their newest text, email or call (the server's `last_reply`), clamped
 * to two lines with the whole snippet on hover. It stays when the row has an AI
 * brief, whose why-line goes under it (owner, 2026-10-07: the recruiters want
 * the actual reply AND the summary). A server that does not send `last_reply`
 * falls back to the last touch.
 */
function LastReplyLine({ c }: { c: ContactListItem }) {
  const reply = c.last_reply !== undefined ? c.last_reply : c.last_comm;
  const text = reply?.body?.trim() || '';
  const Icon = reply?.channel === 'call' ? PhoneMissed : reply?.channel === 'email' ? Mail : MessageSquare;
  return (
    <p
      data-last-reply=""
      title={text || undefined}
      className="mt-0.5 flex min-w-0 items-start gap-1.5 text-xs text-gray-700 dark:text-gray-200"
    >
      <Icon className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
      <span className="sr-only">Their last message: </span>
      <span className="line-clamp-2 min-w-0 break-words">
        {text ? `“${text}”` : reply?.channel === 'call' ? 'Missed call' : 'Inbound message'}
      </span>
    </p>
  );
}

const chipBase = 'inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium';
const CHIP = {
  red: `${chipBase} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`,
  amber: `${chipBase} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`,
  primary: `${chipBase} bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300`,
  gray: `${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`,
};

function Section({
  icon: Icon,
  title,
  count,
  countTitle,
  hint,
  children,
}: {
  icon: LucideIcon;
  title: string;
  count: number;
  /** Tooltip on the "(N)" count — for a section whose scope differs from a
   *  same-named badge elsewhere (see the Needs reply usage below). */
  countTitle?: string;
  hint: string;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <Icon className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        <span className="text-sm text-gray-500 dark:text-gray-400" title={countTitle}>({count})</span>
        <span className="ml-auto hidden text-xs text-gray-400 sm:block">{hint}</span>
      </div>
      <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">{children}</div>
    </section>
  );
}

export default function Today() {
  const navigate = useNavigate();
  const rowNav = useRowNav();
  const { byId, team } = useTeam();
  // "Next up" guided flow: work the composed attention queue item by item
  // (docs/crm-work-queue-plan.md). An empty queue shows a transient note
  // instead of entering the mode.
  const workQueue = useWorkQueue();
  const { user } = useAuth();
  // The ONE breakpoint read: where the header's controls mount (see the header
  // comment). Rows tier themselves with CSS alone.
  const isPhone = useIsPhone();
  const [queueEmptyMsg, setQueueEmptyMsg] = useState(false);

  // WHOSE leads the session works. Empty selection = everyone (the
  // multiselect's "Any" row) — the network-wide toggle; the default is the
  // CRM's historical mine + unassigned. Persisted, because a recruiter
  // covering someone else's desk wants it to stick across page loads.
  const [owners, setOwners] = useState<OwnerSelection>(() => {
    // A STORED empty string is a real choice (everyone), so "never chosen"
    // needs a sentinel the pref can never legitimately hold.
    const NEVER_CHOSEN = '__unset__';
    const stored = getPref('workQueue.owners', NEVER_CHOSEN);
    if (stored === NEVER_CHOSEN) return defaultOwnerSelection(user?.id);
    return stored === '' ? [] : stored.split(',');
  });
  const chooseOwners = (next: OwnerSelection) => {
    setOwners(next);
    setPref('workQueue.owners', next.join(','));
  };
  const ownerOptions = useMemo(() => [
    { value: '', label: 'Everyone (whole network)' },
    ...(user?.id ? [{ value: user.id, label: 'My leads' }] : []),
    { value: UNASSIGNED, label: 'Unassigned' },
    ...team
      .filter((m) => m.id !== user?.id)
      .map((m) => ({ value: m.id, label: m.name })),
  ], [team, user?.id]);

  // Work through serves the needs-action-now tier; the keep-trying leads are
  // offered when those run out (owner 2026-09-28), or started from the
  // all-clear card's own button.
  const startWorking = async (tier: 'now' | 'later' = 'now') => {
    setQueueEmptyMsg(false);
    const count = await workQueue.start(undefined, owners, tier);
    if (count === 0) {
      setQueueEmptyMsg(true);
      window.setTimeout(() => setQueueEmptyMsg(false), 4000);
    }
  };
  const [data, setData] = useState<WorklistResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  // Snooze (#6): leads cleared from today THIS SESSION (for the footer + Undo)
  // and whether the "Cleared for today" footer is expanded. The quick-pick menu
  // itself lives in the shared <SnoozeButton>.
  const [clearedToday, setClearedToday] = useState<ContactListItem[]>([]);
  const [clearedOpen, setClearedOpen] = useState(false);
  // Follow-ups-due count reported by <FollowUpsDuePanel>. The worklist's
  // follow_ups bucket only sees mine+unassigned overdue, so it can read 0 while
  // the panel still has later-today / teammates' follow-ups — this keeps the
  // "all caught up" card from contradicting a populated panel.
  const [followUpsDue, setFollowUpsDue] = useState(0);
  // Referral name mismatches waiting on a phone call, reported by
  // <CollisionTriageCard> (CRM admins only — see that file). Counted here for
  // the same reason as follow-ups: "You're all caught up" must not render above
  // a patient who was promised a call and has not had one.
  const [collisionsDue, setCollisionsDue] = useState(0);
  // People on my outreach lists I can work right now, reported by
  // <OutreachTodaySection>. The worklist buckets deliberately leave list leads
  // out, so without this the "all caught up" card would sit above a list of
  // forty people nobody has called yet.
  const [outreachDue, setOutreachDue] = useState(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/api/crm/worklist');
      setData(res.data as WorklistResponse);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const silent = () => load(true);
    const timer = window.setInterval(silent, 60_000);
    window.addEventListener(STATS_CHANGED_EVENT, silent);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, silent);
    };
  }, [load]);

  /** One-tap callback outcome: optimistic removal, revert by refetch on error. */
  const resolveCallback = async (cb: WorklistCallback, status: 'completed' | 'no_show') => {
    if (busyIds.has(cb.id)) return;
    setBusyIds((prev) => new Set(prev).add(cb.id));
    setData((prev) => prev && ({
      ...prev,
      buckets: {
        ...prev.buckets,
        callbacks: {
          count: Math.max(0, prev.buckets.callbacks.count - 1),
          items: (prev.buckets.callbacks.items || []).filter((c) => c.id !== cb.id),
        },
      },
    }));
    try {
      await api.patch(`/api/crm/callbacks/${cb.id}`, { status });
      notifyStatsChanged();
    } catch {
      load(true);
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(cb.id);
        return n;
      });
    }
  };

  /**
   * Snooze a lead off (or back onto) today's worklist. `untilIso` hides it from
   * all four contact buckets until that moment; `null` un-snoozes (the Undo
   * path). Optimistic: removes the row from those buckets and tracks it in the
   * session "Cleared for today" list; reverts via refetch on error.
   * needs_reply is included (a snooze that left the lead sitting in Needs reply
   * would be pointless) — the patient stays safe because a new inbound wakes
   * the snooze DB-side, so they resurface the moment they message again.
   */
  const snooze = async (c: ContactListItem, untilIso: string | null) => {
    if (busyIds.has(c.id)) return;
    setBusyIds((prev) => new Set(prev).add(c.id));
    if (untilIso) {
      // Optimistically pull it from every contact bucket + remember it.
      setData((prev) => {
        if (!prev) return prev;
        const strip = (bucket: { count: number; items?: ContactListItem[] }) => {
          const present = (bucket.items || []).some((x) => x.id === c.id);
          return present
            ? { count: Math.max(0, bucket.count - 1), items: (bucket.items || []).filter((x) => x.id !== c.id) }
            : bucket;
        };
        return {
          ...prev,
          buckets: {
            ...prev.buckets,
            needs_reply: strip(prev.buckets.needs_reply),
            new_leads: strip(prev.buckets.new_leads),
            follow_ups: strip(prev.buckets.follow_ups),
            stale: strip(prev.buckets.stale),
          },
        };
      });
      setClearedToday((prev) => [{ ...c, snoozed_until: untilIso }, ...prev.filter((x) => x.id !== c.id)]);
    } else {
      // Undo: drop it from the session list; the refetch below restores it to
      // its real bucket.
      setClearedToday((prev) => prev.filter((x) => x.id !== c.id));
    }
    try {
      await api.patch(`/api/crm/contacts/${c.id}`, { snoozed_until: untilIso });
      notifyStatsChanged();
      if (!untilIso) load(true); // un-snooze: pull the row back into its bucket
    } catch {
      if (untilIso) setClearedToday((prev) => prev.filter((x) => x.id !== c.id));
      load(true);
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(c.id);
        return n;
      });
    }
  };

  // AI briefs (docs/plans/crm-brief-plan.md B6): shown only while the
  // worklist says they are on, and only on the four tier-1 lead sections. The
  // brief rides the worklist response (no poll of its own); the server has
  // already ordered each bucket by it.
  const briefOn = data?.brief?.enabled === true;
  /** The brief a tier-1 row shows: null unless briefs are on and it has a why-line. */
  const shownBrief = (brief: Brief | null | undefined): Brief | null =>
    briefOn && brief && briefReasonLabel(brief) ? brief : null;

  /** Shared contact row: identity left, bucket-specific chip right. A shown
   *  `brief` replaces the context line with its why-line and adds the AI mark.
   *  A `reply` line (Needs reply, Keep the conversation going) takes the
   *  context line's place and is never replaced: a shown why-line sits under it. */
  const contactRow = (
    c: ContactListItem,
    context: React.ReactNode,
    chip: React.ReactNode,
    canSnooze = false,
    brief: Brief | null = null,
    reply: React.ReactNode = null,
  ) => {
    const meta = lifecycleMeta(c.lifecycle_status);
    const owner = c.owner_user_id ? byId.get(c.owner_user_id) : null;
    // Only set when this lead is here because its owner is out today.
    const cov = coverageOf(c);
    return (
      <div
        key={c.id}
        role="button"
        tabIndex={0}
        {...rowNav(`/crm/contacts/${c.id}`)}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/crm/contacts/${c.id}`); }}
        className={worklistRowCls}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <RowLink
              to={`/crm/contacts/${c.id}`}
              title={contactName(c)}
              className={`min-w-0 truncate text-sm text-gray-900 dark:text-gray-100 ${needsReply(c.last_comm) ? 'font-semibold' : 'font-medium'}`}
            >
              {contactName(c)}
            </RowLink>
            <span className={`${chipBase} ${meta.badge}`}>{meta.label}</span>
            {!c.owner_user_id && <span className={CHIP.amber}>Unassigned</span>}
            {/* A covered row's owner IS the absent person, so the coverage
                badge replaces the plain owner chip rather than sitting beside
                it — two chips naming the same person, one of them silent about
                why they are on your list, is worse than one that explains. */}
            {cov
              ? <CoverageBadge coverage={cov} />
              : owner && <span className={CHIP.gray}>{owner.name}</span>}
            {/* Phone: the bucket chip ends the name line (ml-auto), so the
                reply, why-line and study chips below run the row's width.
                Desktop keeps it in the right column beside snooze. The chip
                is stateless, so one copy per tier. */}
            <span className="ml-auto flex items-center sm:hidden">{chip}</span>
          </div>
          {reply}
          {(brief || !reply) && (
            <div className="mt-0.5 flex min-w-0 items-center gap-2">
              {brief && <BriefMark />}
              <span className="truncate text-xs text-gray-500 dark:text-gray-400">{brief ? briefReasonLabel(brief) : context}</span>
            </div>
          )}
          <div className="mt-1">
            <StudyChips studies={c.studies} />
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <div className="hidden flex-col items-end gap-1 sm:flex">{chip}</div>
          {canSnooze && (
            <SnoozeButton
              busy={busyIds.has(c.id)}
              onSnooze={(iso) => snooze(c, iso)}
              /* Names the real owner in the aria-label on a covered row:
                 snoozed_until is a column on the lead, so deferring it defers
                 it for the absent owner too. */
              label={cov ? `${contactName(c)} — ${cov.owner_name || 'a teammate'}'s lead` : contactName(c)}
              iconClassName="h-3.5 w-3.5"
              buttonClassName="flex h-11 w-11 items-center justify-center rounded-full sm:h-9 sm:w-9 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-primary-600 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
            />
          )}
        </div>
      </div>
    );
  };

  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const b = data?.buckets;
  // total_actionable is the needs-action-now tier only (the server leaves the
  // keep-trying buckets out of it and the badge).
  const leadsActionable = data
    ? data.total_actionable - data.tasks_due
    : 0;
  const allClear = data ? data.total_actionable === 0 : false;
  // Keep-trying work still listed below: the outreach lists, the Keep trying
  // list and Going stale.
  const keepTryingDue = followUpsDue + outreachDue + (b ? b.stale.count : 0);
  const hotHours = data?.attention?.hot_lead_hours ?? 72;
  const hotWindow = hotHours % 24 === 0 ? `${hotHours / 24} day${hotHours === 24 ? '' : 's'}` : `${hotHours} hours`;
  const liveDays = data?.attention?.conversation_days ?? 7;

  // ---- Header controls: built once, placed per tier ------------------------
  // Desktop: everything in the title row, as before. Phone: the chip beside
  // the (short) title, and whose-leads + Work through + refresh in one toolbar
  // row under the date. Each control is mounted once per tier.
  const emptyNote = queueEmptyMsg ? (
    <span role="status" className="text-xs text-gray-500 dark:text-gray-400">Nothing needs action right now</span>
  ) : null;
  const actionChip = data && !allClear && leadsActionable > 0 ? (
    <span className={CHIP.primary} title="Leads waiting on us, hot new leads and conversations gone quiet">
      {leadsActionable} need{leadsActionable === 1 ? 's' : ''} action now
    </span>
  ) : null;
  // Whose leads the session works. Clearing the selection is the network-wide
  // toggle (the multiselect's "Any" row). Full width on a phone, where it is
  // the toolbar row's flexible item.
  const ownersSelect = !workQueue.active ? (
    <FilterMultiSelect
      auto={!isPhone}
      values={owners}
      onChange={chooseOwners}
      options={ownerOptions}
      label="Whose leads to work through"
      placeholder="Everyone"
      title="Pick whose leads the work session covers"
    />
  ) : null;
  const workThroughButton = !workQueue.active ? (
    <button
      type="button"
      onClick={() => { void startWorking('now'); }}
      disabled={workQueue.starting}
      title="Work through the leads that need action now, one at a time"
      className="inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 sm:min-h-0"
    >
      <Zap className="h-4 w-4" aria-hidden="true" />
      Work through
    </button>
  ) : null;
  const refreshButton = <RefreshButton onClick={() => load()} loading={loading} label="Refresh worklist" />;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Sun}
        title="Today"
        subtitle={today}
        actions={isPhone ? (
          <>
            {actionChip}
            {/* With a session running the toolbar row below is not rendered,
                so refresh keeps its place beside the title. */}
            {workQueue.active && refreshButton}
          </>
        ) : (
          <>
            {emptyNote}
            {actionChip}
            {ownersSelect}
            {workThroughButton}
            {refreshButton}
          </>
        )}
      />

      {/* Phone toolbar row (see the header controls above). */}
      {isPhone && !workQueue.active && (
        <div className="flex items-center gap-2">
          {ownersSelect}
          {workThroughButton}
          <div className="flex-shrink-0">{refreshButton}</div>
        </div>
      )}
      {isPhone && emptyNote}

      {error && (
        <Alert tone="error" icon={false}>
          {error}
          <button onClick={() => load()} className="ml-2 font-medium underline hover:no-underline">Retry</button>
        </Alert>
      )}

      {/* Above My tasks on purpose: this is the only bucket on Today where a
          patient has been told a human will contact them and nothing automated
          will ever do it. */}
      <CollisionTriageCard onCount={setCollisionsDue} />

      <MyTasksPanel showTeamToggle />

      {allClear && collisionsDue === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
          <h2 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">
            {keepTryingDue > 0 ? 'Nothing needs action right now' : "You're all caught up"}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {keepTryingDue > 0
              ? 'Nobody is waiting on a reply or a call, and no new lead or live conversation is due. The leads to keep trying are below.'
              : 'No callbacks, replies, or follow-ups are due. New leads will appear here the moment they arrive.'}
          </p>
          {/* The "no live lead" moment this button exists for: nothing needs
              action now, but the keep-trying leads (no answer yet, gone quiet,
              the safety net's older catches) can still be worked one after
              another. */}
          {!workQueue.active && (
            <>
              <button
                type="button"
                onClick={() => { void startWorking('later'); }}
                disabled={workQueue.starting}
                className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                <Zap className="h-4 w-4" aria-hidden="true" />
                Work the keep-trying list
              </button>
              <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                Covers {owners.length === 0 ? 'the whole network' : 'the recruiters picked in the header'}.
              </p>
            </>
          )}
        </div>
      )}

      {b && (
        <>
          {/* First on purpose (owner rule 2026-08-03): escalated questions run
              a minutes-long countdown before the agent answers on its own, and
              screening-complete leads are waiting on a human decision. Items
              sit here until resolved in the Review queue — presence is
              computed, so completing the work clears the row. */}
          <Section
            icon={Bot}
            title="Agent review queue"
            count={b.agent_review?.count || 0}
            hint="Escalated questions & eligibility decisions — click through to decide"
          >
            {(b.agent_review?.items || []).map((item) => {
              const queueUrl = `/crm/agent?tab=queue&review=${item.conversation_id}`;
              const isScreening = item.kind === 'prescreen_flag';
              const isQuestion = !isScreening && item.kind !== 'eligibility_review' && item.kind !== 'handoff_review' && item.kind !== 'dormant_handback';
              // A scheduler alert (a call_request with its cause) reads its own
              // headline, not "Call requested" (scheduler mode, WP-10 review L2).
              const schedRow = schedulerReviewRow(item.kind, item.cause);
              return (
                <div
                  key={item.conversation_id}
                  role="button"
                  tabIndex={0}
                  {...rowNav(queueUrl)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target === e.currentTarget) navigate(queueUrl);
                  }}
                  className={worklistRowCls}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <RowLink
                        to={queueUrl}
                        title={item.contact_name || 'Unknown lead'}
                        className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100"
                      >
                        {item.contact_name || 'Unknown lead'}
                      </RowLink>
                      <span className={isQuestion ? CHIP.red : CHIP.amber}>{schedRow ? schedRow.label : agentReviewLabel(item.kind)}</span>
                      <CoverageBadge coverage={coverageOf(item)} />
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                      {schedRow
                        ? schedRow.hint
                        : isScreening
                          ? 'An answer looks disqualifying — you can end it early in the Review queue'
                          : isQuestion
                            ? 'The agent is holding for a human — answer it in the Review queue'
                            : 'Screening complete — write the determination in the Review queue'}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-xs text-gray-400">{relTime(item.created_at)}</span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                </div>
              );
            })}
          </Section>

          {/* Finished AI reviews YOU asked for that still want a human. Scoped
              to the requester, not the lead's owner (a review is a question
              you asked), and present until the review is signed off — which
              the server refuses while a flagged criterion is unverified, so
              this clears itself the moment the work is genuinely done. */}
          <Section
            icon={ClipboardCheck}
            title="AI reviews ready"
            count={b.reviews_ready?.count || 0}
            hint="Medical history reviews you started — verify what the AI flagged"
          >
            {(b.reviews_ready?.items || []).map((item) => {
              const resultsUrl = `/crm/contacts/${item.contact_id}/reviews/${item.review_id}`;
              return (
                <div
                  key={item.review_id}
                  role="button"
                  tabIndex={0}
                  {...rowNav(resultsUrl)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target === e.currentTarget) navigate(resultsUrl);
                  }}
                  className={worklistRowCls}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <RowLink
                        to={resultsUrl}
                        title={item.contact_name || 'Unknown lead'}
                        className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100"
                      >
                        {item.contact_name || 'Unknown lead'}
                      </RowLink>
                      <span className={item.overall_result === 'excluded' ? CHIP.red : CHIP.amber}>
                        {reviewResultLabel(item.overall_result)}
                      </span>
                      {item.study_title && (
                        <span className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">{item.study_title}</span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                      {item.pending_count > 0
                        ? `${item.pending_count} criteri${item.pending_count === 1 ? 'on' : 'a'} the AI flagged — verify to sign off`
                        : 'The AI could not call it — read the results and sign off'}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-xs text-gray-400">{relTime(item.completed_at)}</span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                </div>
              );
            })}
          </Section>

          {/* Notes where a teammate named you, and replies to notes you wrote.
              Like the review lane above, scoped to YOU rather than the lead's
              owner; a row opens the lead on that note, which marks it read.
              Renders nothing at zero, and on an api without mentions. */}
          <MentionsLane bucket={b.mentions} nameOf={(uid) => byId.get(uid)?.name ?? null} />

          <Section icon={CalendarClock} title="Callbacks" count={b.callbacks.count} hint="Booked call times — the patient is expecting you">
            {(b.callbacks.items || []).map((cb) => {
              const overdue = new Date(cb.scheduled_at).getTime() < Date.now();
              const busy = busyIds.has(cb.id);
              // Scheduler mode: an assistant booking reads 'Booked by text' and
              // says when the patient was NOT told; every other row as before.
              const origin = callbackOrigin(cb, 'today');
              // The AI brief's why-line, when briefs are on and this lead has one.
              const cbBrief = shownBrief(cb.brief);
              return (
                <div
                  key={cb.id}
                  role="button"
                  tabIndex={0}
                  {...rowNav(`/crm/contacts/${cb.contact.id}`)}
                  onKeyDown={(e) => {
                    // Target guard: Enter on the nested Complete/No-show buttons
                    // must not also navigate (keydown bubbles; stopPropagation
                    // on their onClick doesn't cover it).
                    if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/crm/contacts/${cb.contact.id}`);
                  }}
                  className={worklistRowCls}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <RowLink
                        to={`/crm/contacts/${cb.contact.id}`}
                        title={contactName(cb.contact)}
                        className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100"
                      >
                        {contactName(cb.contact)}
                      </RowLink>
                      <span className={overdue ? CHIP.red : CHIP.primary}>{slotLabel(cb.scheduled_at)}</span>
                      {overdue && <span className={CHIP.red}>overdue</span>}
                      {/* The callback is still assigned to the absent recruiter
                          — you are making the call on their behalf. */}
                      <CoverageBadge coverage={coverageOf(cb)} />
                    </div>
                    {cbBrief ? (
                      // The why-line replaces the booking line; a "patient NOT
                      // told" warning is safety, so it stays beside it.
                      <div className="mt-0.5 flex min-w-0 items-center gap-2">
                        <BriefMark />
                        <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {briefReasonLabel(cbBrief)}
                          {origin.warn && (
                            <>
                              {' · '}
                              <span className="font-medium text-amber-700 dark:text-amber-400">{origin.label}</span>
                            </>
                          )}
                        </span>
                      </div>
                    ) : (
                      <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                        {origin.warn
                          ? <span className="font-medium text-amber-700 dark:text-amber-400">{origin.label}</span>
                          : origin.label}
                        {cb.note ? ` · ${cb.note}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); resolveCallback(cb, 'completed'); }}
                      disabled={busy}
                      title="Mark completed"
                      aria-label={`Mark callback with ${contactName(cb.contact)} completed`}
                      className="flex h-11 w-11 items-center justify-center rounded-full sm:h-9 sm:w-9 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-green-600 hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); resolveCallback(cb, 'no_show'); }}
                      disabled={busy}
                      title="No-show"
                      aria-label={`Mark callback with ${contactName(cb.contact)} as no-show`}
                      className="flex h-11 w-11 items-center justify-center rounded-full sm:h-9 sm:w-9 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-red-600 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
                    >
                      <PhoneMissed className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </Section>

          <Section
            icon={MessageCircleWarning}
            title="Needs reply"
            count={b.needs_reply.count}
            countTitle="Yours & unassigned — the sidebar badge counts the whole network"
            hint="They messaged you — oldest waiting first"
          >
            {/* Their own last message always shows; the AI why-line, when there
                is one, goes under it rather than in its place. */}
            {(b.needs_reply.items || []).map((c) =>
              contactRow(
                c,
                null,
                <span className={CHIP.red}>waiting {relTime(c.needs_reply_at || c.last_activity_at)}</span>,
                true,
                shownBrief(c.brief),
                <LastReplyLine c={c} />,
              )
            )}
          </Section>

          <Section
            icon={Sparkles}
            title="New leads"
            count={b.new_leads.count}
            hint={`Came in the last ${hotWindow} and haven't answered — reach them while they're warm`}
          >
            {(b.new_leads.items || []).map((c) => {
              // A re-submission counts from the new form, not the old record.
              const arrived = leadArrivedAt(c) || c.created_at;
              const resubmitted = arrived !== c.created_at;
              return contactRow(
                c,
                resubmitted
                  ? 'Filled out a form again'
                  : c.attempt_count
                    ? `No answer yet · ${c.attempt_count} ${c.attempt_count === 1 ? 'try' : 'tries'}`
                    : c.studies?.[0]?.lead_source ? `via ${c.studies[0].lead_source}` : 'No contact attempts yet',
                <span className={CHIP.primary}>new · {relTime(arrived)}</span>,
                true,
                shownBrief(c.brief),
              );
            })}
          </Section>

          {/* A live conversation gone quiet (owner 2026-09-28: "ongoing
              conversations are always followed up on without delay"). */}
          <Section
            icon={MessagesSquare}
            title="Keep the conversation going"
            count={b.conversations?.count || 0}
            hint={`They wrote in the last ${liveDays} days and went quiet — nudge them today`}
          >
            {/* What they last said always shows (owner, 2026-10-07), the AI
                why-line under it, as on Needs reply. */}
            {(b.conversations?.items || []).map((c) =>
              contactRow(
                c,
                null,
                <span className={CHIP.amber}>wrote {relTime(c.last_inbound_at)} ago</span>,
                true,
                shownBrief(c.brief),
                <LastReplyLine c={c} />,
              )
            )}
          </Section>

          {/* KEEP TRYING: due another try, but nobody is waiting on us. Listed,
              never in the badge or the header chip. */}
          {keepTryingDue > 0 && (
            <div className="flex items-baseline gap-2 px-1 pt-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Keep trying</h2>
              {/* Explanatory, so desktop-only like the section hints. */}
              <span className="hidden text-xs text-gray-400 dark:text-gray-500 sm:inline">Due another try, but nobody is waiting on us</span>
            </div>
          )}

          {/* A list is a backlog a manager handed this recruiter: first in the
              keep-trying work. Renders nothing when the viewer has no list. */}
          <OutreachTodaySection onCount={setOutreachDue} />

          <FollowUpsDuePanel onCount={setFollowUpsDue} />

          <Section icon={Hourglass} title="Going stale" count={b.stale.count} hint={`No activity for ${data?.stale_days || 4}+ days mid-pipeline`}>
            {(b.stale.items || []).map((c) =>
              contactRow(
                c,
                `In ${lifecycleMeta(c.lifecycle_status).label.toLowerCase()} since ${relTime(c.stage_changed_at || c.created_at)}`,
                <span className={CHIP.gray}>idle {relTime(c.last_activity_at)}</span>,
                true
              )
            )}
          </Section>

          {clearedToday.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <button
                onClick={() => setClearedOpen((v) => !v)}
                className="flex w-full items-center gap-2 border-b border-gray-100 px-4 py-3 text-left transition-colors duration-150 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
              >
                {clearedOpen
                  ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400">Cleared for today</h2>
                <span className="text-sm text-gray-400">({clearedToday.length})</span>
              </button>
              {clearedOpen && (
                <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">
                  {clearedToday.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 py-3">
                      <div className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-600 dark:text-gray-300">{contactName(c)}</span>
                        <p className="mt-0.5 truncate text-xs text-gray-400">
                          Snoozed until {c.snoozed_until ? slotLabel(c.snoozed_until) : 'later'}
                        </p>
                      </div>
                      <button
                        onClick={() => snooze(c, null)}
                        disabled={busyIds.has(c.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        {busyIds.has(c.id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Undo
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
